/**
 * Product video blocks.
 *
 * One media frame stays pinned while the copy beside it scrolls. Whichever block of copy is crossing
 * the middle of the viewport decides which clip plays, so the video reads as an illustration of the
 * paragraph you are on rather than a wall of autoplaying tiles.
 *
 * Markup comes from sections/product-video-blocks.liquid. Nothing here is load bearing: the first
 * clip is marked active server side, so without JavaScript its poster and every word of the copy
 * still render.
 *
 * Playing is deliberately separated from choosing. Choosing happens on any change; playing happens
 * only once the section is laid out and on screen, and is re-asserted rather than fired once, because
 * plenty of things pause a video without telling the page: an app switch, a backgrounded tab, a back
 * navigation, WebKit deciding it has scrolled out of view.
 */

const DEBUG = false;

// Scroll snapping is a single class on the document, so instances have to agree rather than each
// toggle it. Without this an off screen section switches it off for one that is on screen.
const snapWanted = new Set();

class ProductVideoScroller extends HTMLElement {
    connectedCallback() {
        this.parts = Array.from(this.querySelectorAll('[data-part]'));
        this.jumps = Array.from(this.querySelectorAll('[data-jump]'));

        if (this.parts.length === 0) return;

        // Keyed off the attribute rather than document order. A block whose clip is missing or still
        // transcoding renders no video element, and positional indexing would then pair every later
        // block with the next block's clip.
        this.clips = this.parts.map((_, i) => this.querySelector(`[data-clip="${i}"]`));

        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reducedMotion = this.motionQuery.matches;
        this.onMotionChange = (event) => {
            this.reducedMotion = event.matches;
            this.onScroll();
        };
        this.motionQuery.addEventListener('change', this.onMotionChange);
        this.snapMode = this.dataset.snap;
        this.active = -1;

        this.onJump = this.onJump.bind(this);
        this.jumps.forEach((button) => button.addEventListener('click', this.onJump));

        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        // Coming back to a tab, or back to the page, fires no scroll event, and both are places a
        // browser will have paused the clip behind our back.
        document.addEventListener('visibilitychange', this.onScroll);
        window.addEventListener('pageshow', this.onScroll);

        this.update();
    }

    disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        document.removeEventListener('visibilitychange', this.onScroll);
        if (this.motionQuery) this.motionQuery.removeEventListener('change', this.onMotionChange);
        window.removeEventListener('pageshow', this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.setSnapping(false);
        this.jumps.forEach((button) => button.removeEventListener('click', this.onJump));
        this.clips.forEach((clip) => {
            if (clip && typeof clip.pause === 'function') clip.pause();
        });
    }

    onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
        });
    }

    /**
     * Geometry decides both jobs, measured together so they cannot disagree. An observer was doing
     * this before, but acting on its entries picks whichever the browser delivered last, and a jump
     * or a fast scroll puts several in one batch.
     */
    update() {
        // Hidden at this width, so it owns neither the clips nor the page's scrolling. Nothing may
        // play here: at phone widths this whole section sits behind a media query, and a clip started
        // anyway would download in full for a section nobody can see.
        if (this.offsetParent === null) {
            this.setSnapping(false);
            this.stopClips();
            return;
        }

        const box = this.getBoundingClientRect();
        const viewport = window.innerHeight;

        if (box.bottom < 0 || box.top > viewport) {
            this.setSnapping(false);
            this.stopClips();
            return;
        }

        this.pickActive();
        this.playActive();
        this.setSnapping(this.shouldSnap(viewport));
    }

    /**
     * The block being read is the one crossing a line a third of the way down the viewport, which is
     * just below where a block comes to rest when it snaps.
     */
    pickActive() {
        const probe = window.innerHeight * 0.3;
        let index = this.active < 0 ? 0 : this.active;
        let nearest = Infinity;

        for (let i = 0; i < this.parts.length; i += 1) {
            const part = this.parts[i].getBoundingClientRect();

            if (part.top <= probe && part.bottom > probe) {
                index = i;
                nearest = -1;
                break;
            }

            const distance = Math.min(Math.abs(part.top - probe), Math.abs(part.bottom - probe));
            if (nearest !== -1 && distance < nearest) {
                nearest = distance;
                index = i;
            }
        }

        this.setActive(index);
    }

    /**
     * Snapping belongs to the middle of the run and nowhere else. Armed at either end it fights the
     * reader: on the first block it drags them back down when they try to scroll up, and on the last
     * it drags them back up when they try to move on.
     *
     * So it is only on once the first block has started to leave the top of the screen and while the
     * last one still runs off the bottom. A block that is snapped into place sits just under the
     * header, which puts both edges outside that window, so arriving at either end releases it.
     */
    shouldSnap(viewport) {
        if (!this.snapMode || this.snapMode === 'none' || this.reducedMotion) return false;
        if (this.parts.length < 2) return false;

        const edge = 24;
        const first = this.parts[0].getBoundingClientRect();
        const last = this.parts[this.parts.length - 1].getBoundingClientRect();

        const pastTheStart = first.top < -edge;
        const beforeTheEnd = last.bottom > viewport + edge;

        return pastTheStart && beforeTheEnd;
    }

    setSnapping(on) {
        if (on) {
            snapWanted.add(this);
        } else {
            snapWanted.delete(this);
        }
        document.documentElement.classList.toggle('dvf-snapping', snapWanted.size > 0);
    }

    onJump(event) {
        const index = Number(event.currentTarget.dataset.jump);
        const part = this.parts[index];
        if (!part) return;

        part.scrollIntoView({
            behavior: this.reducedMotion ? 'auto' : 'smooth',
            block: 'center',
        });
    }

    /**
     * Re-running an animation needs the class gone and the element reflowed before it goes back on,
     * otherwise the browser sees no change and the swap happens with no movement at all.
     */
    replay(element) {
        if (!element || this.reducedMotion) return;
        element.classList.remove('is-entering');
        void element.offsetWidth;
        element.classList.add('is-entering');
    }

    setActive(index) {
        if (!this.parts[index] || index === this.active) return;
        this.active = index;

        if (DEBUG) console.log('[ProductVideoScroller] active', index);

        this.parts.forEach((part, i) => part.classList.toggle('is-active', i === index));
        this.jumps.forEach((button, i) => {
            const on = i === index;
            button.classList.toggle('is-active', on);
            button.setAttribute('aria-current', on ? 'true' : 'false');
            if (on) this.replay(button.querySelector('.dvf__jump-mark'));
        });

        this.showClip(index);
    }

    /**
     * Only the clip on screen is allowed to download and play. The next one is warmed to metadata so
     * the swap does not land on an empty frame, and nothing beyond that is touched.
     */
    showClip(index) {
        this.clips.forEach((clip, i) => {
            if (!clip) return;

            const on = i === index;
            clip.classList.toggle('is-active', on);

            if (on) {
                this.replay(clip);
            } else {
                clip.classList.remove('is-entering');
                if (typeof clip.pause === 'function') {
                    clip.pause();
                    // Wound back down. A paused clip left at auto keeps pulling the whole file, and a
                    // jump across the index passes through every block on the way.
                    if (clip.preload === 'auto') clip.preload = 'metadata';
                }
            }
        });

        const next = this.clips[index + 1];
        if (next && next.preload === 'none') next.preload = 'metadata';
    }

    /**
     * Idempotent on purpose, so it is safe to call on every frame. Asking a clip that is already
     * playing to play again costs nothing, and asking one the browser quietly paused is the whole
     * point: without this, playback was asserted once per change of block and never again.
     */
    playActive() {
        const clip = this.clips[this.active];
        if (!clip || typeof clip.play !== 'function') return;
        if (this.reducedMotion || this.blocked) return;
        if (!clip.paused) return;

        clip.preload = 'auto';

        const played = clip.play();
        if (played && typeof played.catch === 'function') {
            played.catch((error) => this.onPlayFailure(clip, error));
        }
    }

    stopClips() {
        this.clips.forEach((clip) => {
            if (clip && typeof clip.pause === 'function' && !clip.paused) clip.pause();
        });
    }

    /**
     * The three rejections mean different things and used to be swallowed alike, which is what made
     * a broken clip indistinguishable from a deliberate still.
     */
    onPlayFailure(clip, error) {
        const name = error && error.name;

        // We interrupted ourselves by pausing this clip as the reader moved on. Expected.
        if (name === 'AbortError') return;

        // Policy, not a fault: iOS Low Power Mode, or Safari set to never auto-play this site. It will
        // not change on its own, so stop asking and give the reader a way to start it by hand.
        if (name === 'NotAllowedError') {
            this.blocked = true;
            clip.controls = true;
            return;
        }

        // Anything else is a real fault, most likely a clip with no playable source. Say so even with
        // debug off, because the symptom on the page is an innocent looking poster.
        console.error('[ProductVideoScroller] clip will not play', clip.currentSrc || '(no source)', error);
    }
}

if (!customElements.get('product-video-scroller')) {
    customElements.define('product-video-scroller', ProductVideoScroller);
}

export { ProductVideoScroller };
